import { rebootCpiKiosk, useStoreKiosks } from '@gozoki/api';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '../../components/communication/Toast';
import { getErrorMessageWithSentry } from '../../utils/sentry';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';

export const KiosksManagementPanel = ({ storeReference }: { storeReference: string }) => {
    const [Toast, showToast] = useToast();
    const { data: kiosks, isLoading, refetch } = useStoreKiosks(storeReference);

    const { mutate: mutateRebootKiosk, isPending: isRebooting } = useMutation({
        mutationFn: (kioskId: string) => rebootCpiKiosk(kioskId),
        onSuccess: () => {
            showToast({
                message: 'Kiosk redémarré avec succès',
                severity: 'success',
            });
            refetch();
        },
        onError: (error) => {
            showToast({
                message: getErrorMessageWithSentry(error) || 'Erreur lors du redémarrage',
                severity: 'error',
            });
        },
    });

    const handleReboot = (kioskId: string) => {
        mutateRebootKiosk(kioskId);
    };

    if (isLoading) {
        return (
            <div style={{ padding: '16px' }}>
                <Toast />
                <Typography style={fonts.pageSubtitle}>Gestion des kiosks</Typography>
                <Typography>Chargement des kiosks...</Typography>
            </div>
        );
    }

    return (
        <div style={{ padding: '16px' }}>
            <Toast />
            <Typography style={fonts.pageSubtitle} sx={{ marginBottom: 2 }}>
                Gestion des kiosks
            </Typography>

            {!kiosks || kiosks.length === 0 ? (
                <Card sx={{ marginTop: 2 }}>
                    <CardContent>
                        <Typography color="text.secondary">
                            Aucun kiosk trouvé pour ce magasin
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {kiosks.map((kiosk) => (
                        <Card key={kiosk.id} sx={{ minWidth: 275 }}>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{ marginBottom: 1 }}
                                        >
                                            {kiosk.name}
                                        </Typography>
                                        <Typography color="text.secondary" sx={{ marginBottom: 1 }}>
                                            ID: {kiosk.id}
                                        </Typography>
                                        <Chip label="Kiosk CPI" color="primary" size="small" />
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<RestartAltIcon />}
                                        onClick={() => handleReboot(kiosk.id)}
                                        disabled={isRebooting}
                                        sx={{
                                            'marginLeft': 2,
                                            'minWidth': 140,
                                            '&:hover': {
                                                backgroundColor: ThemeColors.ANTI_GASPI_ORANGE,
                                                color: ThemeColors.WHITE,
                                            },
                                        }}
                                    >
                                        {isRebooting ? 'Redémarrage...' : 'Redémarrer'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}
        </div>
    );
};
